import * as THREE from 'three';

/**
 * landmarks/texturing.ts — gives the hand-built low-poly landmarks real surface
 * texture (brick / stone / wood / plaster / roof tile / concrete) without any UV work, by TRIPLANAR mapping in
 * world space inside MeshStandardMaterial.onBeforeCompile. World-space tiling
 * means a brick is a constant real size on every landmark whatever its scale, and
 * the box/cylinder geometry needs no unwrap. Textures are CC0 (Poly Haven), shared
 * across all landmarks (~0.4 MB total), and applied once per cached material.
 *
 * Materials are classified by name + PBR props: glowing windows/neon, glass, metal,
 * ice, foliage and anything ambiguous are LEFT ALONE (they read better clean). Only
 * confident masonry/brick/wood surfaces get a texture, tinted by the material's own
 * colour so each landmark keeps its palette.
 */
const BASE = import.meta.env.BASE_URL;
const loader = new THREE.TextureLoader();

export type Role =
  | 'brick'
  | 'stone'
  | 'wood'
  | 'plaster'
  | 'rooftile'
  | 'concrete'
  | 'clapboard';
const FOLDER: Record<Role, string> = {
  brick: 'lm_brick',
  stone: 'lm_stone',
  wood: 'lm_wood',
  plaster: 'lm_plaster',
  rooftile: 'lm_rooftile',
  concrete: 'lm_concrete',
  clapboard: 'lm_clapboard',
};
/** World units per texture tile (smaller = bigger features). */
const SCALE: Record<Role, number> = {
  brick: 4.6,
  stone: 7.5,
  wood: 6.0,
  plaster: 7.0,
  rooftile: 3.4,
  concrete: 9.0,
  // 12 boards per tile → ~0.33u exposure. Deliberately a touch oversized vs a
  // real 0.15m clapboard so the lap lines still read from the road.
  clapboard: 4.0,
};

const cache = new Map<Role, THREE.Texture>();
function tex(role: Role): THREE.Texture {
  let t = cache.get(role);
  if (!t) {
    t = loader.load(`${BASE}textures/${FOLDER[role]}/albedo.jpg`);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 4;
    t.userData.shared = true; // never freed by the per-city disposer
    cache.set(role, t);
  }
  return t;
}

const SKIP =
  /glass|water|win|sign|neon|lamp|ball|lant|glow|marq|foliage|green|pitch|ice|snow|terra|cap|bone|dark|sand|rock|granite|indigo|smoke|crater|cable|steel|gold|bronze|metal|red/;
const WOOD = /bark|timber|wood|verm/;
const BRICK = /brick|brownstone/;
const ROOFTILE = /tile|redroof|slate/; // checked BEFORE skip: 'redroof' contains 'red'
// Lapped timber siding (M119). Hamilton Grange is a Federal-style WOOD house and
// shipped with its body named 'pale', which STONE claims — so it read as masonry.
// Generic lm_wood grain is not the fix either: clapboard's read is the stack of
// horizontal boards, each shadowing the one below.
const CLAPBOARD = /clap|siding|weatherboard/;
// 'portland' (GS6): only St Paul's carries it (verified across all 48 GLBs) and
// QA wants pale Portland limestone, not the rubble-stone courses lm_stone paints
// — routed to plaster. Its 'stoneL' body/dome share that name with Tower Bridge
// + Tower of London (whose rubble read QA explicitly KEEPS), so stoneL stays
// stone; the St Paul's remainder is a Blender material-rename (city round).
const PLASTER = /white|cream|ochre|plaster|render|stucco|portland/; // whitewashed/lime walls
const CONCRETE = /conc/;
const STONE = /stone|gothic|pale|grey|gray|buff|tufa|basalt|marble|castle/;

/** Exported for tests — the name/PBR → triplanar-role mapping is a contract:
 *  landmark GLB material names are authored against these regexes, and
 *  externally-imported baked models rely on MISSING them (see
 *  LandmarkModelOpts.skipTexturing for the explicit bypass). */
export function classify(m: THREE.MeshStandardMaterial): Role | null {
  const e = m.emissive;
  if (e && e.r + e.g + e.b > 0.05) return null; // glowing
  if (m.metalness > 0.5) return null; // metal
  if (m.roughness < 0.32) return null; // glass
  const n = (m.name || '').toLowerCase();
  if (WOOD.test(n)) return 'wood';
  if (BRICK.test(n)) return 'brick';
  if (ROOFTILE.test(n)) return 'rooftile';
  if (CLAPBOARD.test(n)) return 'clapboard';
  if (SKIP.test(n)) return null;
  if (PLASTER.test(n)) return 'plaster';
  if (CONCRETE.test(n)) return 'concrete';
  if (STONE.test(n)) return 'stone';
  return null; // unknown → leave clean (safe default)
}

interface Taggable {
  __lmTextured?: boolean;
}

export function applyLandmarkTextures(model: THREE.Object3D): void {
  model.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mm of mats) {
      const m = mm as THREE.MeshStandardMaterial & Taggable;
      if (!m || m.__lmTextured) continue;
      m.__lmTextured = true; // guard: cached materials are shared across instances
      const role = classify(m);
      if (!role) continue;
      m.map = tex(role);
      const s = 1 / SCALE[role];
      m.onBeforeCompile = (shader) => {
        shader.vertexShader = shader.vertexShader
          .replace(
            '#include <common>',
            '#include <common>\nvarying vec3 vLmWP;\nvarying vec3 vLmWN;',
          )
          .replace(
            '#include <project_vertex>',
            '#include <project_vertex>\n vLmWP=(modelMatrix*vec4(transformed,1.0)).xyz; vLmWN=normalize(mat3(modelMatrix)*objectNormal);',
          );
        shader.fragmentShader = shader.fragmentShader
          .replace(
            '#include <common>',
            `#include <common>\nuniform float uLmScale;\nvarying vec3 vLmWP;\nvarying vec3 vLmWN;`,
          )
          .replace(
            '#include <map_fragment>',
            `vec3 lw=abs(vLmWN); lw=pow(lw,vec3(4.0)); lw/=(lw.x+lw.y+lw.z)+1e-4;
             vec3 lt=texture2D(map,vLmWP.zy*uLmScale).rgb*lw.x+texture2D(map,vLmWP.xz*uLmScale).rgb*lw.y+texture2D(map,vLmWP.xy*uLmScale).rgb*lw.z;
             diffuseColor.rgb*=lt*1.75;`,
          );
        shader.uniforms.uLmScale = { value: s };
      };
      m.needsUpdate = true;
    }
  });
}
